import { useCallback, useEffect, useMemo, useState } from "react";

import Maths from "./Maths";
import {
  api,
  type Capacite,
  type EtatCompetencesQcm,
  type PropositionQcm,
  type ReferentielQcm,
} from "./api";

const QUESTIONS_PAR_COMPETENCE = 3;

/** Reproduit les contrôles du serveur, pour signaler pendant l'édition.
 *
 *  Les avertissements sont recalculés ici plutôt que repris de la réponse
 *  du serveur : ils portent sur le regroupement, c'est-à-dire précisément ce
 *  que le professeur est en train de modifier. Figés, ils continueraient à
 *  dénoncer une compétence de deux questions qu'il vient d'en pourvoir d'une
 *  troisième.
 */
function controler(
  referentiel: ReferentielQcm,
  etat: EtatCompetencesQcm,
  capacites: Set<string>,
) {
  const erreurs: string[] = [];
  const avertissements: string[] = [];
  const neuves = etat.questions.filter((q) => q.capacites.length === 0);
  const proposees = new Map(
    referentiel.capacites_par_question.map((c) => [c.ref, c.capacites]),
  );

  for (const question of neuves) {
    if (!proposees.has(question.ref)) {
      erreurs.push(`Question neuve sans capacité : ${question.ref}`);
    }
  }
  for (const [ref, codes] of proposees) {
    for (const code of codes) {
      if (!capacites.has(code)) {
        erreurs.push(`${ref} cite une capacité inexistante : ${code}`);
      }
    }
  }

  const groupees = new Set(referentiel.competences.flatMap((c) => c.refs));
  for (const question of etat.questions) {
    if (!groupees.has(question.ref)) {
      erreurs.push(`Question sans compétence : ${question.ref}`);
    }
  }

  const binaire = new Map(etat.questions.map((q) => [q.ref, q.reponses <= 2]));
  for (const competence of referentiel.competences) {
    if (competence.refs.length === 0) {
      erreurs.push(`Compétence ${competence.code} n'évalue aucune question`);
    } else if (competence.refs.length < QUESTIONS_PAR_COMPETENCE) {
      avertissements.push(
        `${competence.code} n'évalue que ${competence.refs.length} question(s) : ` +
          "ses quatre niveaux de maîtrise ne sont pas tous atteignables",
      );
    }
    if (
      competence.refs.length > 0 &&
      competence.refs.every((ref) => binaire.get(ref))
    ) {
      avertissements.push(
        `${competence.code} n'est évaluée que par des questions binaires : ` +
          "on y atteint 50 % au hasard, soit le troisième des quatre niveaux",
      );
    }
  }

  const [minimum, maximum] = etat.fourchette;
  const nombre = referentiel.competences.length;
  if (nombre < minimum || nombre > maximum) {
    avertissements.push(
      `${nombre} compétences pour ${etat.questions.length} questions, hors de ` +
        `la fourchette indicative (${minimum}–${maximum})`,
    );
  }

  // Ces propositions-là ne seront pas appliquées : la banque a déjà tranché.
  // Une seule ligne, car relancer une identification sur un QCM enregistré
  // mettrait toutes les questions dans ce cas.
  const rattachees = etat.questions.filter(
    (q) => q.capacites.length > 0 && proposees.has(q.ref),
  );
  if (rattachees.length > 0) {
    avertissements.push(
      `${rattachees.length} question(s) déjà rattachée(s) : la proposition ne ` +
        "remplacera pas leurs capacités",
    );
  }

  return { erreurs, avertissements };
}

export default function CompetencesQcm({ qcmId }: { qcmId: number }) {
  const [etat, setEtat] = useState<EtatCompetencesQcm | null>(null);
  const [capacites, setCapacites] = useState<Capacite[]>([]);
  const [referentiel, setReferentiel] = useState<ReferentielQcm | null>(null);
  const [proposition, setProposition] = useState<PropositionQcm | null>(null);
  const [travail, setTravail] = useState<"repos" | "appel" | "enregistrement">(
    "repos",
  );
  const [message, setMessage] = useState<string | null>(null);

  const recharger = useCallback(() => {
    api.lireCompetencesQcm(qcmId).then(setEtat);
    api.capacitesQcm(qcmId).then(setCapacites).catch(() => undefined);
    // Une proposition déjà archivée se relit sans refacturer d'appel.
    api
      .relirePropositionQcm(qcmId)
      .then((p) => {
        setProposition(p);
        setReferentiel(p.referentiel);
      })
      .catch(() => undefined);
  }, [qcmId]);

  useEffect(recharger, [recharger]);

  const parCode = useMemo(
    () => new Map(capacites.map((c) => [c.code, c])),
    [capacites],
  );
  const { erreurs, avertissements } = useMemo(
    () =>
      referentiel && etat
        ? controler(referentiel, etat, new Set(capacites.map((c) => c.code)))
        : { erreurs: [], avertissements: [] },
    [referentiel, etat, capacites],
  );

  const identifier = async () => {
    setTravail("appel");
    setMessage(null);
    try {
      const p = await api.identifierCompetencesQcm(qcmId);
      setProposition(p);
      setReferentiel(p.referentiel);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setTravail("repos");
    }
  };

  const enregistrer = async () => {
    if (!referentiel) return;
    setTravail("enregistrement");
    setMessage(null);
    try {
      const bilan = await api.enregistrerCompetencesQcm(qcmId, referentiel);
      setMessage(
        `${bilan.competences} compétences enregistrées, ${bilan.capacites} ` +
          `rattachement(s) de capacité écrit(s).` +
          (bilan.avertissements.length
            ? ` ${bilan.avertissements.length} point(s) d'attention.`
            : ""),
      );
      setEtat(await api.lireCompetencesQcm(qcmId));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setTravail("repos");
    }
  };

  const modifier = (transformer: (r: ReferentielQcm) => ReferentielQcm) =>
    setReferentiel((precedent) => (precedent ? transformer(precedent) : precedent));

  /** Déplacer une question : une question de QCM n'appartient qu'à une
   *  compétence — elle n'a qu'une bonne réponse et ne se partage pas. */
  const deplacer = (ref: string, code: string) =>
    modifier((r) => ({
      ...r,
      competences: r.competences.map((c) => ({
        ...c,
        refs:
          c.code === code
            ? c.refs.includes(ref)
              ? c.refs
              : [...c.refs, ref]
            : c.refs.filter((x) => x !== ref),
      })),
    }));

  const modifierCapacites = (ref: string, codes: string[]) =>
    modifier((r) => ({
      ...r,
      capacites_par_question: r.capacites_par_question.some((c) => c.ref === ref)
        ? r.capacites_par_question.map((c) =>
            c.ref === ref ? { ...c, capacites: codes } : c,
          )
        : [...r.capacites_par_question, { ref, capacites: codes }],
    }));

  if (!etat) return <p>Chargement…</p>;

  const neuves = etat.questions.filter((q) => q.capacites.length === 0);
  const [minimum, maximum] = etat.fourchette;
  const capacitesProposees = new Map(
    (referentiel?.capacites_par_question ?? []).map((c) => [c.ref, c.capacites]),
  );

  return (
    <section className="competences">
      <header className="saisie-entete">
        <div>
          <h2>Compétences — {etat.titre}</h2>
          <p className="sous-titre">
            {etat.questions.length} questions, dont {neuves.length} sans capacité
            {proposition?.origine === "archive" &&
              " · proposition relue sans nouvel appel"}
            {proposition?.cout_dollars != null &&
              ` · appel facturé ${proposition.cout_dollars.toFixed(3)} $`}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={identifier} disabled={travail === "appel"}>
            {travail === "appel"
              ? "Appel en cours…"
              : referentiel
                ? "Relancer l'identification (payant)"
                : "Identifier les compétences (payant)"}
          </button>
          <button
            type="button"
            onClick={enregistrer}
            disabled={!referentiel || erreurs.length > 0 || travail !== "repos"}
            title={erreurs.length > 0 ? "Corriger les erreurs d'abord" : undefined}
          >
            Enregistrer
          </button>
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      {!referentiel && (
        <p className="aide">
          Aucune identification lancée. L'appel à Claude n'écrit rien : vous
          relisez et corrigez avant d'enregistrer. Il ne propose des capacités
          que pour les <strong>questions neuves</strong> — celles qui arrivent
          déjà rattachées de la banque gardent les leurs, sans quoi une question
          resservie changerait d'axe d'une année sur l'autre.
        </p>
      )}

      {erreurs.length > 0 && (
        <div className="anomalies erreurs">
          <strong>À corriger avant d'enregistrer</strong>
          <ul>
            {erreurs.slice(0, 8).map((erreur) => (
              <li key={erreur}>{erreur}</li>
            ))}
            {erreurs.length > 8 && <li>… et {erreurs.length - 8} autres</li>}
          </ul>
        </div>
      )}

      {avertissements.length > 0 && (
        <div className="anomalies avertissements">
          <strong>Points d'attention</strong>
          <ul>
            {avertissements.map((avertissement) => (
              <li key={avertissement}>{avertissement}</li>
            ))}
          </ul>
        </div>
      )}

      {referentiel && (
        <>
          <h3>Compétences proposées</h3>
          <p className="aide">
            Une compétence de moins de {QUESTIONS_PAR_COMPETENCE} questions ne
            dit pas grand-chose : les réponses d'un QCM valent tout ou rien, si
            bien qu'avec deux questions un élève ne peut obtenir que 0, 50 ou
            100 %, et deux des quatre niveaux de maîtrise deviennent
            inatteignables. Fourchette visée ici : {minimum} à {maximum}.
          </p>
          {referentiel.competences.map((competence) => (
            <article key={competence.code} className="fiche-competence">
              <header>
                <strong>{competence.code}</strong>
                <input
                  value={competence.libelle}
                  onChange={(e) =>
                    modifier((r) => ({
                      ...r,
                      competences: r.competences.map((c) =>
                        c.code === competence.code
                          ? { ...c, libelle: e.target.value }
                          : c,
                      ),
                    }))
                  }
                  aria-label={`Libellé de ${competence.code}`}
                />
                <span
                  className={
                    competence.refs.length < QUESTIONS_PAR_COMPETENCE
                      ? "chiffres attente"
                      : "chiffres"
                  }
                >
                  {competence.refs.length} questions
                </span>
              </header>
              <p className="justification">{competence.justification}</p>
            </article>
          ))}

          <h3>Questions</h3>
          <p className="aide">
            La compétence se choisit d'un clic — une question n'en porte qu'une.
            Les capacités du programme, elles, s'attachent à la question
            elle-même et la suivront dans les prochains QCM : celles qui sont
            déjà fixées apparaissent en gris et ne se modifient pas ici.
          </p>
          <table className="questions-qcm competences-qcm">
            <tbody>
              {etat.questions.map((question) => {
                const fixees = question.capacites;
                const codes = fixees.length
                  ? fixees
                  : (capacitesProposees.get(question.ref) ?? []);
                const porteuse = referentiel.competences.find((c) =>
                  c.refs.includes(question.ref),
                );
                return (
                  <tr key={question.ref}>
                    <th>Q{question.numero}</th>
                    <td>
                      <div className="enonce-qcm">
                        <Maths texte={question.enonce} syntaxe="typst" />
                      </div>
                      <div className="fichier">
                        {question.ref}
                        {question.reponses <= 2 && " · binaire"}
                      </div>
                      <div className="capacites">
                        {codes.length === 0 && (
                          <span className="attente">Aucune capacité</span>
                        )}
                        {codes.map((code) => (
                          <span
                            key={code}
                            className={
                              fixees.length ? "puce-capacite figee" : "puce-capacite"
                            }
                            title={
                              (parCode.get(code)?.libelle ?? code) +
                              (fixees.length ? " — fixée par la banque" : "")
                            }
                          >
                            {code}
                            {fixees.length === 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  modifierCapacites(
                                    question.ref,
                                    codes.filter((x) => x !== code),
                                  )
                                }
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {fixees.length === 0 && (
                          <select
                            value=""
                            aria-label={`Ajouter une capacité à ${question.ref}`}
                            onChange={(e) => {
                              const code = e.target.value;
                              if (!code || codes.includes(code)) return;
                              modifierCapacites(question.ref, [...codes, code]);
                            }}
                          >
                            <option value="">+ capacité…</option>
                            {capacites.map((capacite) => (
                              <option key={capacite.code} value={capacite.code}>
                                {capacite.code} — {capacite.libelle.slice(0, 70)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                    <td>
                      {referentiel.competences.map((competence) => (
                        <button
                          key={competence.code}
                          type="button"
                          className={
                            porteuse?.code === competence.code ? "puce active" : "puce"
                          }
                          title={competence.libelle}
                          onClick={() => deplacer(question.ref, competence.code)}
                        >
                          {competence.code}
                        </button>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {!referentiel && etat.competences.length > 0 && (
        <>
          <h3>Compétences enregistrées</h3>
          <table className="questions-qcm">
            <tbody>
              {etat.competences.map((competence) => (
                <tr key={competence.code}>
                  <th>{competence.code}</th>
                  <td>
                    <div className="enonce-qcm">{competence.libelle}</div>
                    <div className="fichier">
                      {competence.refs.length} questions ·{" "}
                      {competence.capacites.map((c) => c.code).join(", ") ||
                        "aucune capacité"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
